<template>
  <v-form ref="form" v-model="formValid" validate-on="submit" @submit.prevent>
    <v-text-field
      v-model="localValue.dnsServer"
      variant="outlined"
      class="mb-2"
      :persistent-placeholder="false"
      :rules="[dnsServerRule]"
      :aria-label="$t('modals.addOrEditConditionalForwarding.dnsServer')"
    >
      <template #label>
        {{ $t("modals.addOrEditConditionalForwarding.dnsServer") }}
        <span class="label-example">{{
          $t("modals.addOrEditConditionalForwarding.dnsServerExample")
        }}</span>
      </template>
      <template #message="{ message }">
        <div class="d-flex align-center">
          <v-icon size="x-small" icon="icon-warning-xs" class="mr-1" />
          <span>{{ message }}</span>
        </div>
      </template>
    </v-text-field>
    <v-text-field
      v-model="localValue.localDomainName"
      variant="outlined"
      persistent-hint
      :persistent-placeholder="false"
      :hint="$t('modals.addOrEditConditionalForwarding.localDomainNameHint')"
      :rules="[localDomainNameRule]"
      :aria-label="$t('modals.addOrEditConditionalForwarding.localDomainName')"
    >
      <template #label>
        {{ $t("modals.addOrEditConditionalForwarding.localDomainName")
        }}<span class="label-example">
          {{ $t("modals.addOrEditConditionalForwarding.localDomainNameExample") }}</span
        >
      </template>
      <template #message="{ message }">
        <div class="d-flex align-center">
          <v-icon size="x-small" icon="icon-warning-xs" class="mr-1" />
          <span>{{ message }}</span>
        </div>
      </template>
    </v-text-field>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, ref, type PropType, watch } from "vue";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import Patterns from "@/constants/patterns.ts";
import { useI18n } from "vue-i18n";
import type { VuetifyFormRef } from "@/types.ts";
import type { ConditionalForwardingItem } from "@/_store/network/swg/conditional-forwarding.module.ts";

export default defineComponent({
  emits: ["update:localValue", "update:valid"],
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const form = ref<VuetifyFormRef>();
    const { t } = useI18n();

    const existingItem = props.config.item as ConditionalForwardingItem | undefined;
    const localValue = ref({
      dnsServer: existingItem?.dnsServer ?? "",
      localDomainName: existingItem?.localDomainName ?? "",
    });
    const formValid = ref(true);

    const dnsServerRule = (value: string) => {
      if (!value) return t("validations.required");
      if (!Patterns.IP_V4.test(value)) {
        return t("validations.ipAddress");
      }
      return true;
    };

    const localDomainNameRule = (value: string) => {
      // Left it here to specifically point out that the field is not required, no value means okay, valu present must be validated though
      if (!value) return true;
      if (!Patterns.DOMAIN.test(value)) {
        return t("validations.domain");
      }
      return true;
    };

    const validate = async () => {
      const { valid } = (await form.value?.validate()) ?? { valid: false };
      return valid;
    };

    watch(
      () => localValue.value,
      () => {
        emit("update:localValue", { ...localValue.value });
        form.value?.resetValidation();
      },
      { deep: true }
    );

    return {
      localValue,
      formValid,
      dnsServerRule,
      localDomainNameRule,
      form,
      validate,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(.v-messages .icon-warning-xs.v-icon) {
  font-size: 15px !important;

  &:before {
    color: rgb(var(--v-theme-red-dark));
  }
}

:deep(.v-input:not(.v-input--error) .v-messages .icon-warning-xs.v-icon) {
  &:before {
    color: rgb(var(--v-theme-indigo-medium));
  }
}

:deep(.v-field--active .label-example) {
  display: none;
}
</style>
